'use client';

import cx from 'classnames';
import PropTypes from 'prop-types';
import {useEffect, useRef, useState} from 'react';

export const Button = ({
  id,
  className,
  variant = 'primary',
  size = 'small',
  tight = false,
  onClick,
  onMouseEnter = null,
  onMouseLeave = null,
  children,
  processingChildren = null,
  processingIcon = <span className="evt-spinner inline" />,
  formSubmit = false,
  fullWidth = false,
  disabled = false,
  type = 'button',
  icon = null,
  override = false,
  noTextWrap = false,
  disableProcessing = false,
  ariaLabel,
  title = '',
  dataClipboardText = '',
  'data-testid': dataTestId = '',
  'data-qa-id': dataQaId,
  'data-qa-value': dataQaValue,
  'data-impression-tracking': dataImpressionTracking = '',
  'data-qa-tigran': dataQaTigran = '',
  rounded = false,
  tabIndex = 0,
  blurry = false,
  ref = null,
  style = undefined,
}) => {
  const [processing, setProcessing] = useState(false);
  const mountedRef = useRef(true);

  const onClickFn = async (e) => {
    // If the props.onClick handler returns a promise we wait until it completes before allowing another click to go through.
    if (processing) return false;
    const value = onClick?.(e);
    if (value && typeof value.then === 'function') {
      if (!disableProcessing) {
        setProcessing(true);
      }
      if (override) {
        e.preventDefault();
        e.stopPropagation();
      }
      value.finally(() => {
        if (mountedRef.current) {
          setProcessing(false);
        }
      });
    }
    return value;
  };

  useEffect(
    () =>
      // track when we are unmounted so we don't update the state after un-mounting
      () => {
        mountedRef.current = false;
      },
    []
  );

  return (
    <button
      id={id}
      data-qa-id={dataQaId}
      data-impression-tracking={dataImpressionTracking?.toString()}
      data-qa-value={dataQaValue?.toString()}
      data-qa-tigran={dataQaTigran || undefined}
      data-testid={dataTestId || undefined}
      data-clipboard-text={dataClipboardText || undefined}
      className={cx('button', className, variant || 'primary', size, {
        // If size is not defined or is not small or large, fall back to small.
        // This prevents the "small" class from being double-applied when size="small"
        small: !size || !['small', 'medium', 'large'].includes(size),
        wide: fullWidth,
        tight,
        nowrap: noTextWrap,
        processing,
        'icon-only': icon && !children,
        rounded,
      })}
      onClick={onClick ? onClickFn : undefined}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      disabled={disabled}
      type={type ?? (formSubmit ? 'submit' : 'button')}
      title={title || null}
      aria-label={ariaLabel || title}
      ref={ref}
      tabIndex={tabIndex}
      style={style}
    >
      {processing && processingIcon}
      {icon}
      {processing && processingChildren ? processingChildren : children}
    </button>
  );
};

Button.propTypes = {
  id: PropTypes.string,
  className: PropTypes.string,
  variant: PropTypes.oneOf([
    'primary',
    'secondary',
    'tertiary',
    'blurred',
    'raised',
    'transparent',
    'unstyled-button',
    'link',
    'featured', // currently used for the CF's goh & cohost buttons
  ]),
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  tight: PropTypes.bool,
  onClick: PropTypes.func,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  processingChildren: PropTypes.oneOfType([PropTypes.element, PropTypes.string]),
  processingIcon: PropTypes.element,
  children: PropTypes.oneOfType([PropTypes.element, PropTypes.node, PropTypes.string]),
  formSubmit: PropTypes.bool,
  fullWidth: PropTypes.bool,
  disabled: PropTypes.bool,
  'data-qa-id': PropTypes.string.isRequired,
  'data-impression-tracking': PropTypes.string,
  'data-qa-value': PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  type: PropTypes.string,
  icon: PropTypes.element,
  override: PropTypes.bool,
  noTextWrap: PropTypes.bool,
  disableProcessing: PropTypes.bool,
  title: PropTypes.string,
  ariaLabel: PropTypes.string,
  rounded: PropTypes.bool,
  tabIndex: PropTypes.number,
};
